import React, { ReactNode } from 'react';

/**
 * Minimal, security-first markdown renderer for untrusted banner content.
 *
 * Why not reuse `logic/markdown/markdown.ts`?
 * That helper does `html-react-parser(marked(content))`. `marked` emits raw HTML
 * and `html-react-parser` does NOT sanitize it — any `<img onerror>`, inline
 * `<script>`-adjacent vectors, or `javascript:` hrefs in the source would be
 * rendered verbatim. For backend-controlled, potentially attacker-influenced
 * content we therefore build the React tree DIRECTLY from a tiny token grammar
 * and never produce an HTML string. The only elements this can emit are
 * <p>, <br>, <strong> and a locked-down <a> — nothing else is possible.
 *
 * Supported syntax (by design, nothing more):
 *   - **bold** / __bold__
 *   - [label](url)
 *   - blank line  -> new paragraph
 *   - single \n   -> <br/>
 */

/** Matches a bold run or a link, capturing their inner parts. */
const TOKEN_RE = /\*\*([\s\S]+?)\*\*|__([\s\S]+?)__|\[([^\]]+)\]\(([^)\s]+)\)/g;

/**
 * Normalise a markdown link target into a safe href, or return null to drop it.
 *
 * Rules (per spec): force https, reject every non-http(s) scheme so that
 * `javascript:`, `data:`, `vbscript:`, `file:` etc. can never reach the DOM.
 */
export const sanitizeUrl = (rawUrl: string): string | null => {
  const url = rawUrl.trim();

  // Explicitly reject dangerous schemes (incl. with embedded whitespace/case tricks).
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) && !/^https?:\/\//i.test(url)) {
    return null;
  }

  // Already absolute http(s): upgrade http -> https.
  if (/^https?:\/\//i.test(url)) {
    return url.replace(/^http:\/\//i, 'https://');
  }

  // Protocol-relative ("//host/path") -> https.
  if (url.startsWith('//')) {
    return `https:${url}`;
  }

  // Bare domain ("www.example.com/path") -> https.
  if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(url)) {
    return `https://${url}`;
  }

  // Anything else (relative paths, anchors, mailto, …) is not an external https
  // link, so we drop the link and keep the label as plain text.
  return null;
};

/** Parse a single line into React nodes, resolving bold and links. */
const parseLine = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Fresh regex instance so the shared `lastIndex` state never leaks across calls.
  const re = new RegExp(TOKEN_RE.source, 'g');

  while ((match = re.exec(text)) !== null) {
    const [full, boldStar, boldUnderscore, linkLabel, linkUrl] = match;

    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const boldInner = boldStar ?? boldUnderscore;
    if (boldInner != null) {
      nodes.push(<strong key={nodes.length}>{parseLine(boldInner)}</strong>);
    } else if (linkLabel != null && linkUrl != null) {
      const href = sanitizeUrl(linkUrl);
      if (href) {
        nodes.push(
          <a
            key={nodes.length}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {parseLine(linkLabel)}
          </a>
        );
      } else {
        // Unsafe target: keep the human-readable label, discard the link.
        nodes.push(...parseLine(linkLabel));
      }
    }

    lastIndex = match.index + full.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

/** Render one paragraph, turning single newlines into <br/>. */
const renderParagraph = (block: string): ReactNode[] => {
  const lines = block.split('\n');
  return lines.flatMap((line, i) => {
    const parsed = parseLine(line);
    return i < lines.length - 1
      ? [...parsed, <br key={`br-${i}`} />]
      : parsed;
  });
};

export interface SafeMarkdownProps {
  content: string;
  className?: string;
}

/**
 * Renders trusted-syntax / untrusted-source markdown. Emits only <p>, <br>,
 * <strong> and sanitized <a> — never raw HTML.
 */
export const SafeMarkdown: React.FC<SafeMarkdownProps> = ({ content, className }) => {
  const paragraphs = content.split(/\n{2,}/).filter((block) => block.trim().length > 0);

  return (
    <div className={className}>
      {paragraphs.map((block, i) => (
        <p key={i}>{renderParagraph(block)}</p>
      ))}
    </div>
  );
};
